/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { KeyboardEventHandler, ReactNode, useEffect } from "react";
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  FormControl,
  FormHelperText,
  TextField
} from "@mui/material";
import validatorStyle from "./validatorStyle";
import { ValidatorType } from "../../types/ValidatorType";
import { FormChangeEventHandler, UseForm } from "../../types/UseForm";
import { AutoCompleteOptionWithValue } from "../../types/AutoCompleteOptionWithValue";

type ValidatorAutoCompleteProps<T> = {
  label: string;
  name: string;
  value: UseForm["values"];
  onChange: FormChangeEventHandler;
  registerValidators?: Function;
  validators?: Array<ValidatorType>;
  dependentValidationFields?: Array<string>;
  helperText?: string;
  isHighlighted?: boolean;
  error?: boolean;
  disabled?: boolean;
  formValueTransformer?: Function;
  extractNewValue: Function;
  getSelectedValue: Function;
  endAdornment?: ReactNode;
  onInputKeyDown?: KeyboardEventHandler<HTMLInputElement>,
  renderInput?: (params: AutocompleteRenderInputParams, label: string)=> ReactNode,
} & Omit<AutocompleteProps<T, boolean, boolean, boolean>, "onChange" | "renderInput">;

const ValidatorAutoComplete = <T = AutoCompleteOptionWithValue>({
  label,
  name,
  value,
  onChange,
  registerValidators,
  validators = [],
  dependentValidationFields = [],
  helperText,
  isHighlighted,
  error,
  disabled,
  options,
  formValueTransformer,
  extractNewValue,
  getSelectedValue,
  endAdornment,
  onInputKeyDown,
  renderInput,
  ...rest
}: ValidatorAutoCompleteProps<T>) => {
  const selectedObj = getSelectedValue(options, value);

  useEffect(() => {
    if (registerValidators && validators && !disabled) {
      registerValidators(name, validators, dependentValidationFields);
    }
    return () => {
      registerValidators && registerValidators(name, []);
    };
  }, [registerValidators, dependentValidationFields, name, disabled]);

  return (
    <FormControl
      fullWidth
      error={error}
      sx={isHighlighted && (value === null || value === "") ? validatorStyle.mandatory : undefined}
    >
      <Autocomplete<T, boolean, boolean, boolean>
        {...rest}
        value={selectedObj}
        disabled={disabled}
        disableClearable
        options={options}
        noOptionsText={"Keine Optionen verfügbar"}
        onChange={(_, newValue) => {
          onChange({
            target: {
              name: name,
              value: extractNewValue(newValue),
            },
          });
        }}
        renderInput={(params)=> renderInput?.(params, label) ?? <TextField {...params} label={label} />}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default ValidatorAutoComplete;
